// V11.14 — Helper client-side pour calculer la "raison" affichée
// sous un athlète mis à la UNE. Utilisé en fallback quand l'API n'a pas
// fourni `feature_reason` (typiquement quand on affiche depuis /athletes
// et pas depuis /athletes/featured/now).
//
// Logique alignée avec `_build_feature_reason` côté backend :
//   1. Achievement médaillé (or > argent > bronze) de l'année en cours
//   2. Achievement non médaillé de l'année en cours (title / competition)
//   3. Fallback activité générique
//
// Note : les événements calendrier (stages / camps) ne sont pas
// disponibles côté client → si l'API `/featured/now` a pu les inclure
// on garde son `feature_reason`, sinon on retombe sur cette version
// achievements-only.

const MEDAL_ORDER = { or: 3, argent: 2, bronze: 1 };
const MEDAL_LABEL = { or: "Médaille d'OR", argent: "Médaille d'ARGENT", bronze: "Médaille de BRONZE" };

/**
 * @param {object} athlete
 * @param {number} [currentYear]
 * @returns {{text:string, kind:string, date_label:string|null, medal:string|null}|null}
 */
export function computeFeatureReason(athlete, currentYear) {
  if (!athlete) return null;
  const year = currentYear || new Date().getFullYear();
  const acs = athlete.achievements || [];
  let bestMedal = null;
  let bestNoMedal = null;
  for (const ach of acs) {
    if (ach.hidden) continue;
    if (Number(ach.year) !== year) continue;
    const m = (ach.medal || "").toString().trim().toLowerCase() || null;
    if (m && MEDAL_ORDER[m]) {
      if (!bestMedal || MEDAL_ORDER[m] > MEDAL_ORDER[bestMedal.medal]) {
        bestMedal = { ...ach, medal: m };
      }
    } else {
      if (!bestNoMedal || Number(ach.month || 0) > Number(bestNoMedal.month || 0)) {
        bestNoMedal = ach;
      }
    }
  }
  const best = bestMedal || bestNoMedal;
  if (best) {
    const parts = [];
    if (best.medal && MEDAL_LABEL[best.medal]) parts.push(MEDAL_LABEL[best.medal]);
    const title = best.title || best.competition || "Nouveau résultat";
    parts.push(title);
    const details = [];
    if (best.competition && best.competition !== title) details.push(best.competition);
    if (best.category) details.push(best.category);
    if (best.place) details.push(best.place);
    const text = parts.join(" · ") + (details.length ? ` (${details.join(" · ")})` : "");
    const month = Number(best.month || 0);
    const dateLabel = month ? `${String(month).padStart(2, "0")}/${year}` : String(year);
    return {
      text,
      kind: best.medal ? "medal" : "achievement",
      date_label: dateLabel,
      medal: best.medal || null,
    };
  }
  const disc = athlete.main_discipline || (athlete.disciplines || [])[0];
  return {
    text: `Activité ${year}${disc ? ` en ${disc}` : ""}`,
    kind: "activity",
    date_label: String(year),
    medal: null,
  };
}
